<script setup>
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Check, Pencil, X } from 'lucide-vue-next'
import { ref, computed } from 'vue'
import { MapSource, MapLayer } from '@/components/map'
import { calculateArea, formatArea, hasAreaGeometry, isClosedLineString } from '@/utils/geometry'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  map: {
    type: Object,
    required: true
  },
  multiSelect: {
    type: Boolean,
    default: false
  },
  defaultPreview: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select', 'toggle', 'delete', 'edit'])

const isHovered = ref(false)
const showPreview = computed(() => props.defaultPreview || props.isSelected || isHovered.value)

const onClick = () => {
  emit(props.multiSelect ? 'toggle' : 'select', props.feature)
}

// Search results carry Nominatim's display_name ("Great Britain, United Kingdom"): lead with the place, trail its parents.
const nameParts = computed(() => {
  const name = props.feature.properties?.name
  if (!name) return { title: `Drawn area ${props.index + 1}`, parents: null }
  if (!props.feature.properties?.isSearch) return { title: name, parents: null }
  const [title, ...rest] = name.split(', ')
  return { title, parents: rest.length ? rest[rest.length - 1] : null }
})

const size = computed(() => {
  const geometry = props.feature.geometry
  if (hasAreaGeometry(geometry) || isClosedLineString(geometry)) {
    return formatArea(calculateArea(geometry))
  }
  return props.feature.properties?.areaText || null
})

const subtitle = computed(() => [
  props.feature.properties?.addresstype,
  nameParts.value.parents,
  props.feature.properties?.fileName,
  size.value,
].filter(Boolean).join(' · '))

const canEdit = computed(() => props.feature.properties?.isDrawn || props.feature.properties?.isBoundary)
</script>

<template>
  <div
    role="button"
    tabindex="0"
    :aria-pressed="!!isSelected"
    class="group flex items-center gap-3 px-3 py-2 border-b last:border-b-0 cursor-pointer transition-colors focus-visible:outline-none focus-visible:bg-muted/50"
    :class="isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'"
    @click="onClick"
    @keydown.enter.self.prevent="onClick"
    @keydown.space.self.prevent="onClick"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <Checkbox v-if="multiSelect" :model-value="!!isSelected" class="pointer-events-none" tabindex="-1" />
    <Check v-else class="w-4 h-4 shrink-0" :class="isSelected ? 'text-primary' : 'invisible'" />

    <div class="min-w-0 flex-1">
      <p class="text-sm truncate" :class="isSelected ? 'font-semibold' : 'font-medium'">{{ nameParts.title }}</p>
      <p v-if="subtitle" class="text-xs text-muted-foreground truncate first-letter:uppercase">{{ subtitle }}</p>
    </div>

    <div class="flex items-center shrink-0" :class="{ 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100': !isSelected }">
      <Button
        v-if="canEdit"
        type="button"
        variant="ghost"
        size="icon"
        class="h-7 w-7 text-muted-foreground"
        title="Edit shape"
        @click.stop="emit('edit', feature)"
      >
        <Pencil class="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        class="h-7 w-7 text-muted-foreground"
        title="Remove"
        @click.stop="emit('delete', feature)"
      >
        <X class="h-4 w-4" />
      </Button>
    </div>

    <MapSource
      v-if="showPreview"
      :map="map"
      type="geojson"
      :id="`preview-feature-${index}`"
      :data="{
        type: 'FeatureCollection',
        features: [feature]
      }"
      :options="{
        'generateId': true
      }"
    >
      <MapLayer
        :map="map"
        :id="`preview-feature-fill-${index}`"
        type="fill"
        :source="`preview-feature-${index}`"
        :options="{
          'paint': {
            'fill-color': '#059669',
            'fill-opacity': 0.1
          },
          'filter': ['==', '$type', 'Polygon']
        }"
      ></MapLayer>

      <MapLayer
        :map="map"
        :id="`preview-feature-line-${index}`"
        type="line"
        :source="`preview-feature-${index}`"
        :options="{
          'paint': {
            'line-color': '#059669',
            'line-width': 2
          }
        }"
      ></MapLayer>
    </MapSource>
  </div>
</template>
